import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const EVENT_SOURCE = 'pawwji-matching-set';

// Timings match Horizon's product form and add-to-cart button.
const ADDED_STATE_RESET_DELAY = 800;
const SUCCESS_MESSAGE_DURATION = 5000;
const ERROR_MESSAGE_DURATION = 10000;

/**
 * Matching set pair: keeps the set total in step with the chosen variants and adds both products to the
 * cart in one request. The cart change is announced with the same CartLinesUpdateEvent flow as Horizon's
 * product form, so the cart icon and the cart drawer contents update as they do after a regular add to cart;
 * then the cart opens through Horizon's openCart action. Without JavaScript the form posts to /cart/add.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The pair form.
 * @property {(HTMLSelectElement | HTMLInputElement)[]} variantInputs - One variant field per product, in cart order.
 * @property {HTMLElement} total - The formatted set total.
 * @property {HTMLButtonElement} addButton - Submits the pair.
 * @property {HTMLElement} status - Live region for the add result.
 *
 * @extends {Component<Refs>}
 */
class MatchingSetComponent extends Component {
  requiredRefs = ['form', 'variantInputs', 'total', 'addButton', 'status'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number[]} */
  #timeouts = [];

  connectedCallback() {
    super.connectedCallback();

    // The action is the no-JavaScript fallback. Once this component owns the submit, drop it: Shopify's
    // analytics treats every /cart/add form as a single-product form and logs errors for this two-item one.
    // Adds made with fetch are still tracked by Shopify.
    this.refs.form.removeAttribute('action');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#clearTimeouts();
  }

  /**
   * Shows the chosen variant's image in its row, then updates the total.
   * @param {Event} event - The change event from a variant select.
   */
  handleVariantChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions[0];
    const thumb = select.closest('.pawwji-matching-set__row')?.querySelector('img.pawwji-matching-set__thumb');
    if (option?.dataset.image && thumb instanceof HTMLImageElement) thumb.src = option.dataset.image;

    this.#renderTotal();
  }

  /**
   * Adds both chosen variants to the cart without leaving the page.
   * @param {SubmitEvent} event - The form submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { addButton } = this.refs;
    if (this.#controller || addButton.disabled) return;

    const items = this.refs.variantInputs.map((input) => ({ id: Number(input.value), quantity: 1 }));
    if (items.length === 0 || items.some((item) => !item.id)) return;

    const controller = new AbortController();
    this.#controller = controller;
    this.#clearTimeouts();
    this.#setStatus('');
    addButton.setAttribute('aria-disabled', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      /** @type {{ items: typeof items, sections?: string }} */
      const body = { items };
      const sections = this.#cartSectionIds();
      if (sections) body.sections = sections;

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        this.#resolveCartUpdate(deferred, items.length, true);
        this.#showError(data.description || data.message);
        return;
      }

      this.#showAdded();
      this.#resolveCartUpdate(deferred, items.length, false, data.sections).then((updated) => {
        if (updated) this.#openCart();
      });
    } catch (error) {
      deferred.reject(error);
      if (error instanceof Error && error.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: (error instanceof Error && error.message) || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError();
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
        addButton.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * Section ids of the cart components on the page, so their markup comes back with the add request.
   * @returns {string}
   */
  #cartSectionIds() {
    const ids = new Set();

    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
    }

    return [...ids].join(',');
  }

  /**
   * Fetches the cart the same way Horizon's product form does.
   * @returns {Promise<any>}
   */
  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if ('fetchCartData' in cartItems && typeof cartItems.fetchCartData === 'function') {
        return cartItems.fetchCartData();
      }
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }

  /**
   * Settles the cart update event with the fresh cart so listeners can update.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferred - The event's deferred promise.
   * @param {number} itemCount - The number of items requested.
   * @param {boolean} didError - Whether the add failed.
   * @param {Record<string, string>} [sections] - Section markup returned by the add request.
   * @returns {Promise<boolean>} Whether listeners received the fresh cart.
   */
  #resolveCartUpdate(deferred, itemCount, didError, sections) {
    return this.#fetchCart()
      .then((cart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError,
            items: cart.items,
            source: EVENT_SOURCE,
            sourceId: this.id,
            itemCount,
            sections,
          },
        });
        return true;
      })
      .catch((error) => {
        deferred.reject(error);
        return false;
      });
  }

  /**
   * Opens the cart with Horizon's configured openCart action (the cart drawer, or the cart page without one).
   * Skipped when the drawer is already open, for example through the "auto-open cart drawer" theme setting:
   * reopening an open drawer replays its opening animation.
   */
  #openCart() {
    const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
      document.querySelector('theme-drawer#cart-drawer')
    );
    if (drawer?.hasAttribute('open')) return;

    const actions = /** @type {any} */ (window).Shopify?.actions;

    if (typeof actions?.openCart === 'function') {
      Promise.resolve(actions.openCart()).catch(() => {});
      return;
    }

    drawer?.open?.();
  }

  async #showAdded() {
    const { addButton } = this.refs;

    this.#setStatus(this.dataset.addedMessage ?? '');
    this.#timeouts.push(setTimeout(() => this.#setStatus(''), SUCCESS_MESSAGE_DURATION));

    addButton.dataset.added = 'true';
    await yieldToMainThread();
    await onAnimationEnd(addButton);
    this.#timeouts.push(setTimeout(() => addButton.removeAttribute('data-added'), ADDED_STATE_RESET_DELAY));
  }

  /**
   * @param {string} [message] - The error from the cart response.
   */
  #showError(message) {
    this.#setStatus(message || this.dataset.errorMessage || '', true);
    this.#timeouts.push(setTimeout(() => this.#setStatus(''), ERROR_MESSAGE_DURATION));
  }

  /**
   * Writes to the live region. Errors are shown; success messages are for screen readers only.
   * @param {string} text - The message.
   * @param {boolean} [isError] - Whether the message is an error.
   */
  #setStatus(text, isError = false) {
    const { status } = this.refs;

    status.textContent = text;
    status.classList.toggle('visually-hidden', !isError || !text);
  }

  #renderTotal() {
    const { moneyFormat, currency } = this.dataset;
    if (!moneyFormat) return;

    const cents = this.refs.variantInputs.reduce((sum, input) => {
      const source = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
      return sum + (Number(source?.dataset.price) || 0);
    }, 0);

    this.refs.total.textContent = formatMoney(cents, moneyFormat, currency ?? '');
  }

  #clearTimeouts() {
    for (const timeout of this.#timeouts) clearTimeout(timeout);
    this.#timeouts = [];
  }
}

if (!customElements.get('pawwji-matching-set-component')) {
  customElements.define('pawwji-matching-set-component', MatchingSetComponent);
}
